import { ReactElement, useCallback } from "react";

import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  Text,
  useBreakpointValue,
  VStack,
  ModalBody,
  ModalFooter,
  useToast,
  ButtonGroup,
  UseDisclosureProps,
  Button,
} from "@chakra-ui/react";

import { mobileStyleBreakpoint } from "util/styleHelpers";
import useRemoveUser from "hooks/api/customers/useRemoveUser";
import useSelectedCustomerCode from "hooks/useSelectedCustomerCode";
import { useAuth } from "contexts/AuthProvider";

export const modalPadding = mobileStyleBreakpoint("6", "6");

export const REMOVE_USER_ERROR_MESSAGE =
  "Another admin must remove you from the portal";

const RemoveUserConfirmation = ({
  selectedUserId,
  isOpen,
  onClose,
  customerName,
}: {
  selectedUserId?: string;
  customerName: string;
} & Required<Pick<UseDisclosureProps, "isOpen" | "onClose">>): ReactElement => {
  const { userId: loggedInUser } = useAuth();

  const selectedCustomerCode = useSelectedCustomerCode()[0];

  const toast = useToast();

  const modalSize = useBreakpointValue({ base: "xs", md: "xl" });

  const { mutateAsync: removeUser, isLoading } = useRemoveUser(
    selectedUserId,
    selectedCustomerCode
  );

  const clickHandler = useCallback(() => {
    if (loggedInUser === selectedUserId) {
      toast({
        title: "Error",
        description: REMOVE_USER_ERROR_MESSAGE,
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top",
      });
      onClose();
    }

    return removeUser()
      .then(() => {
        toast({
          title: "Success!",
          description: `${customerName} has been removed from this customer code.`,
          status: "success",
          duration: 4000,
          isClosable: true,
          position: "top",
        });
        onClose();
      })
      .catch(() => {
        toast({
          title: "Error",
          description: "Failed to remove user, please try again",
          status: "error",
          duration: 3000,
          isClosable: true,
          position: "top",
        });
      });
  }, [onClose, customerName, loggedInUser, removeUser, selectedUserId, toast]);

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        isCentered
        motionPreset="slideInBottom"
        size={modalSize}
      >
        <ModalOverlay />
        <ModalContent>
          <VStack spacing={4} display="left" h="100%">
            <ModalHeader
              display="left"
              h="100%"
              p={6}
              style={{
                paddingTop: "14px",
                paddingBottom: "0",
                marginTop: "0",
              }}
              textStyle="h4"
            >
              Remove User
            </ModalHeader>
          </VStack>
          <ModalBody p={modalPadding}>
            <Text textStyle="body2" color="gray.700">
              This will remove {customerName} from this customer code.
            </Text>
          </ModalBody>
          <ModalFooter width="100%" p={6}>
            <ButtonGroup display="right" spacing={4}>
              <Button variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button
                isLoading={isLoading}
                isDisabled={isLoading}
                variant="danger"
                colorScheme="red"
                onClick={clickHandler}
              >
                Remove
              </Button>
            </ButtonGroup>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default RemoveUserConfirmation;
